import { fetchChats } from "@/core/api/chatApi";
import { useAuth } from "@/core/hooks/useAuth";
import api from "@/core/api/axios";

import { AttendantStatus, DocID } from "@/types";
import { ChatWithLastAttendance } from "@/types";
import { getNextOffset } from "@/shared/utils";
import {
  type InfiniteData,
  type QueryClient,
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getPaginationParams } from "../shared/utils/pagination";
import { realtimeQueryOptions } from "@/core/hooks/useApiQueries/realtimeQueryOptions";
import { useSectorConnection } from "@/core/providers/SectorConnectionProvider/context";
import type { ChatScopeKeyParams } from "@/features/chat/utils/chatScope";
import { keepReadRowOnRefetch } from "@/features/chat/utils/unreadList";
import { useChatUnreadFilter } from "@/features/chat/stores/useChatUnreadFilter";

export type ChatListParams = {
  sectorId: DocID;
  attendantFilters?: AttendantStatus[];
  search?: string;
  tagIds?: DocID[];
  /** Recorte da lista (SA-1367); sem ele, "Todas". */
  chatScope?: ChatScopeKeyParams;
  /** SA-1455: só as com não lida. */
  unreadOnly?: boolean;
};

/**
 * Chave de uma lista de conversas. O socket lê os filtros daqui
 * (`updateCacheFor`, `belongsToScope`) para inserir e tirar linhas, e o
 * pré-carregamento do boot (SA-1452) monta a MESMA chave sem o hook.
 */
export const chatListQueryKey = ({
  sectorId,
  attendantFilters,
  search,
  tagIds,
  chatScope,
  unreadOnly,
}: ChatListParams) => {
  const scope = chatScope?.scope ?? "all";
  return [
    "chats",
    {
      sectorId: sectorId ?? null,
      attendantFilters: attendantFilters?.slice().sort(),
      search: search ?? "",
      tagIds: tagIds?.slice().sort() ?? [],
      // O socket lê o recorte daqui (`belongsToScope`) para inserir e
      // remover linhas; as áreas já vêm resolvidas e ordenadas.
      scope,
      areaIds:
        scope === "areas" ? (chatScope?.areaIds?.slice().sort() ?? []) : [],
      // O socket também lê daqui: lista de não lidas só ganha linha com
      // não lida.
      unread: unreadOnly ?? false,
    },
  ] as const;
};

/**
 * Query de uma lista de conversas, para o hook e para o boot. Sem `userId` (o
 * boot ainda não tem o `/user/me`) só o recorte "Atribuídas a mim" falha: é o
 * único que manda o usuário no filtro.
 */
export const chatListQueryOptions = (
  params: ChatListParams & {
    userId?: DocID;
    token: string | undefined;
    queryClient: QueryClient;
  }
) => {
  const {
    sectorId,
    attendantFilters,
    search,
    tagIds,
    chatScope,
    unreadOnly = false,
    userId,
    token,
    queryClient,
  } = params;

  return infiniteQueryOptions({
    queryKey: chatListQueryKey(params),
    initialPageParam: 0,
    queryFn: async ({ pageParam, queryKey }) => {
      if (!sectorId) throw new Error("Sector ID is required");
      if (chatScope?.scope === "assigned" && !userId) {
        throw new Error("User not authenticated");
      }
      const page = await fetchChats(
        userId ?? "",
        token,
        pageParam,
        sectorId,
        attendantFilters,
        search,
        tagIds,
        chatScope,
        undefined,
        unreadOnly
      );
      if (!unreadOnly) return page;
      return keepReadRowOnRefetch(
        page,
        queryClient.getQueryData<InfiniteData<typeof page>>(queryKey),
        pageParam,
        useChatUnreadFilter.getState().keptReadChatId
      );
    },
    getNextPageParam: (lastPage) => {
      const { start, limit, total } = getPaginationParams(
        lastPage.meta.pagination
      );

      return getNextOffset(start, limit, total);
    },
    // SA-1455: religar "Não lidas" volta para o cache sem rebuscar (a lista
    // custa o setor inteiro no servidor). O socket mantém esse cache inativo
    // em dia e `pruneReadFromUnreadLists` tira as lidas na religada.
    ...realtimeQueryOptions,
  });
};

export const useChats = (
  sectorId?: DocID,
  attendantFilters?: AttendantStatus[],
  search?: string,
  tagIds?: DocID[],
  /** Recorte da lista (SA-1367); sem ele, "Todas". */
  chatScope?: ChatScopeKeyParams,
  /**
   * `enabled: false` segura a query (a da busca só roda com texto).
   * `unreadOnly`: só as com não lida (SA-1455).
   */
  options?: { enabled?: boolean; unreadOnly?: boolean }
) => {
  const { user, token } = useAuth();
  const { connected } = useSectorConnection();
  const queryClient = useQueryClient();

  const infiniteQuery = useInfiniteQuery({
    ...chatListQueryOptions({
      sectorId: sectorId as DocID,
      attendantFilters,
      search,
      tagIds,
      chatScope,
      unreadOnly: options?.unreadOnly ?? false,
      userId: user?.documentId,
      token,
      queryClient,
    }),
    enabled: !!user && !!sectorId && connected && (options?.enabled ?? true),
  });

  const createChatMutation = useMutation({
    mutationFn: async ({
      phoneNumber,
      instanceId: instance,
    }: {
      phoneNumber: string;
      instanceId: string;
    }) => {
      // A rota devolve o MESMO shape de `/chats/:chatId/detail` (SA-1395). Antes
      // era um `as ChatWithLastAttendance` em cima do chat cru, sem os campos
      // quentes do Redis, e essa resposta é o que semeia o cache do chat aberto.
      const response = await api().post<{ data: ChatWithLastAttendance }>(
        "/chats/create",
        { phoneNumber, instanceId: instance },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    },
    mutationKey: ["chats"],
  });

  return { ...infiniteQuery, createChatMutation };
};
